import { z } from "zod";

const slugSchema = z
  .string()
  .transform((val) => (val.trim() === "" ? undefined : val.trim()))
  .pipe(
    z
      .string()
      .min(2, "Slug must be at least 2 characters")
      .max(200, "Slug must be at most 200 characters")
      .regex(/^[a-z0-9-]+$/, "Slug must be lowercase letters, numbers, and hyphens only")
      .optional(),
  );

const revocationPolicySchema = z.enum(["immediate", "end_of_period", "grace_period"]);

// Podcast categories following Apple Podcasts taxonomy
export const PODCAST_CATEGORIES = [
  "Arts",
  "Business",
  "Comedy",
  "Education",
  "Fiction",
  "Government",
  "Health & Fitness",
  "History",
  "Kids & Family",
  "Leisure",
  "Music",
  "News",
  "Religion & Spirituality",
  "Science",
  "Society & Culture",
  "Sports",
  "Technology",
  "True Crime",
  "TV & Film",
] as const;

// Common languages
export const PODCAST_LANGUAGES = [
  { code: "en", name: "English" },
  { code: "es", name: "Spanish" },
  { code: "fr", name: "French" },
  { code: "de", name: "German" },
  { code: "it", name: "Italian" },
  { code: "pt", name: "Portuguese" },
  { code: "ja", name: "Japanese" },
  { code: "ko", name: "Korean" },
  { code: "zh", name: "Chinese" },
  { code: "ru", name: "Russian" },
  { code: "ar", name: "Arabic" },
  { code: "hi", name: "Hindi" },
] as const;

export const createPodcastSchema = z.object({
  name: z
    .string()
    .min(2, "Podcast name must be at least 2 characters")
    .max(200, "Podcast name must be at most 200 characters"),
  slug: slugSchema,
  description: z.string().max(5000, "Description must be at most 5000 characters").optional(),
  coverArtworkUrl: z.string().url("Must be a valid URL").max(500).optional().or(z.literal("")),
  language: z.string().min(2).max(10).default("en"),
  category: z.string().max(100).optional(),
  explicit: z.coerce.boolean().default(false),
  timezone: z.string().max(50).default("UTC"),
  isPremium: z.coerce.boolean().default(false),
  revocationPolicy: revocationPolicySchema.default("end_of_period"),
  revocationGraceDays: z.coerce.number().int().min(0).max(90).default(7),
});

export const updatePodcastSchema = z.object({
  name: z
    .string()
    .min(2, "Podcast name must be at least 2 characters")
    .max(200, "Podcast name must be at most 200 characters")
    .optional(),
  slug: slugSchema,
  description: z.string().max(5000).nullable().optional(),
  coverArtworkUrl: z.string().url("Must be a valid URL").max(500).nullable().optional(),
  language: z.string().min(2).max(10).optional(),
  category: z.string().max(100).nullable().optional(),
  explicit: z.coerce.boolean().optional(),
  timezone: z.string().max(50).optional(),
  isPremium: z.coerce.boolean().optional(),
  revocationPolicy: revocationPolicySchema.optional(),
  revocationGraceDays: z.coerce.number().int().min(0).max(90).optional(),
});

export type CreatePodcastFormData = z.infer<typeof createPodcastSchema>;
export type UpdatePodcastFormData = z.infer<typeof updatePodcastSchema>;
